import React, { useMemo, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface MarkdownWithTocProps {
  content: string;
}

// Extract #, ##, ### headings, generate stable anchor IDs.
// Skip headings inside fenced code blocks (``` or ~~~) so code comments
// like "# 使用示例" are not mistaken for document headings.
function extractToc(markdown: string): TocItem[] {
  const items: TocItem[] = [];
  let inCodeBlock = false;

  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();

    // Toggle fenced code block state (``` or ~~~)
    if (/^(```|~~~)/.test(trimmed)) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const headingMatch = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (!headingMatch) continue;

    const level = headingMatch[1].length; // 1, 2, or 3
    const text = headingMatch[2].trim();
    // Generate slug: lowercase, replace spaces/special chars with hyphens
    const id = text
      .toLowerCase()
      .replace(/[^\w\u4e00-\u9fff\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    items.push({ id, text, level });
  }
  return items;
}

const MarkdownWithToc = ({ content }: MarkdownWithTocProps) => {
  const toc = useMemo(() => extractToc(content), [content]);
  const [activeId, setActiveId] = useState<string>('');

  // IntersectionObserver: highlight current heading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-20px 0px -70% 0px' }
    );

    const elements = toc.map((item) => document.getElementById(item.id)).filter(Boolean);
    elements.forEach((el) => observer.observe(el!));

    return () => observer.disconnect();
  }, [toc]);

  const handleTocClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  if (toc.length === 0) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={codeComponents}
      >
        {content}
      </ReactMarkdown>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: 0 }}>
      {/* TOC sidebar */}
      <nav
        style={{
          width: 220,
          flexShrink: 0,
          borderRight: '1px solid #f0f0f0',
          padding: '12px 8px 12px 0',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 250px)',
          position: 'sticky',
          top: 0,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: '#999', marginBottom: 8, paddingLeft: 12 }}>
          目录
        </div>
        {toc.map((item) => (
          <div
            key={item.id}
            onClick={() => handleTocClick(item.id)}
            style={{
              padding: '4px 12px',
              paddingLeft: item.level === 3 ? 24 : item.level === 2 ? 16 : 8,
              fontSize: item.level === 1 ? 13 : 13,
              fontWeight: item.level === 1 ? 600 : 400,
              lineHeight: '20px',
              cursor: 'pointer',
              color: activeId === item.id ? '#1677ff' : item.level === 1 ? '#333' : '#555',
              backgroundColor: activeId === item.id ? '#e6f4ff' : 'transparent',
              borderRadius: 4,
              marginTop: item.level === 1 ? 8 : 0,
              borderTop: item.level === 1 ? '1px solid #f0f0f0' : 'none',
              paddingTop: item.level === 1 ? 8 : 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: 'all 0.15s',
            }}
            title={item.text}
          >
            {item.text}
          </div>
        ))}
      </nav>

      {/* Content area */}
      <div style={{ flex: 1, minWidth: 0, padding: '0 0 0 24px' }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            ...codeComponents,
            h1: ({ children, ...props }: any) => {
              const text = String(children);
              const id = text
                .toLowerCase()
                .replace(/[^\w\u4e00-\u9fff\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              return <h1 id={id} {...props}>{children}</h1>;
            },
            h2: ({ children, ...props }: any) => {
              const text = String(children);
              const id = text
                .toLowerCase()
                .replace(/[^\w\u4e00-\u9fff\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              return <h2 id={id} {...props}>{children}</h2>;
            },
            h3: ({ children, ...props }: any) => {
              const text = String(children);
              const id = text
                .toLowerCase()
                .replace(/[^\w\u4e00-\u9fff\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              return <h3 id={id} {...props}>{children}</h3>;
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

// Shared code renderer
const codeComponents = {
  code: ({ node, inline, className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    if (!inline && match) {
      return React.createElement(
        SyntaxHighlighter as any,
        {
          style: vscDarkPlus,
          language: match[1],
          PreTag: 'div' as any,
          ...props,
        },
        String(children).replace(/\n$/, '')
      );
    }
    return (
      <code className={className} {...(props as any)}>
        {children}
      </code>
    );
  },
};

export default MarkdownWithToc;
